import { useEffect, useState } from "react";

import { CardContent } from "@/components/ui/card";
import type { FlashcardWord } from "@/types/common";

import { FlashCard } from "./FlashCard";

type CardStackProps = {
  words: FlashcardWord[];
  currentIndex: number;
  isFlipped: boolean;
  frontSide: "english" | "korean";
  trackCategories: boolean;
  onFlip: () => void;
  onSwipe: (dir: "left" | "right") => void;
  onDragUpdate: (dir: "left" | "right" | null) => void;
  onSpeak: () => void;
  isFavorited: (id: string) => boolean;
  onToggleFavorite: (id: string) => void;
};

const STACK_CONFIG = [
  { scale: 1, yOffset: 0, zIndex: 30 },
  { scale: 0.95, yOffset: 12, zIndex: 20 },
  { scale: 0.9, yOffset: 24, zIndex: 10 },
] as const;

export function CardStack({
  words,
  currentIndex,
  isFlipped,
  frontSide,
  trackCategories,
  onFlip,
  onSwipe,
  onDragUpdate,
  onSpeak,
  isFavorited,
  onToggleFavorite,
}: CardStackProps) {
  const [swipingOutAtIndex, setSwipingOutAtIndex] = useState<number | null>(null);
  const isSwipingOut = swipingOutAtIndex === currentIndex;

  useEffect(() => {
    // 덱/인덱스가 바뀌면 스와이프 아웃 로컬 플래그만 초기화
    // eslint-disable-next-line react-hooks/set-state-in-effect -- 의도된 UI 동기 리셋
    setSwipingOutAtIndex(null);
  }, [currentIndex, words]);

  const visibleWords = words.slice(currentIndex, currentIndex + 3);

  return (
    <CardContent className="flex-1 min-h-0 flex flex-col items-center justify-center px-5 sm:aspect-3/4 sm:max-h-fit">
      <div className="relative h-full min-h-0 w-full">
        {[...visibleWords].reverse().map((word, reversedIdx) => {
          const stackIdx = visibleWords.length - 1 - reversedIdx;
          const config = STACK_CONFIG[stackIdx] ?? STACK_CONFIG[STACK_CONFIG.length - 1];
          const isTop = stackIdx === 0;
          const isNextTop = stackIdx === 1;
          const isTextVisible = isTop || (isNextTop && isSwipingOut);

          return (
            <FlashCard
              key={word.id}
              word={word}
              isFlipped={isTop ? isFlipped : false}
              isInteractive={isTop}
              isTextVisible={isTextVisible}
              frontSide={frontSide}
              trackCategories={trackCategories}
              onFlip={isTop ? onFlip : () => {}}
              onSwipe={isTop ? onSwipe : () => {}}
              onSwipeStart={isTop ? () => setSwipingOutAtIndex(currentIndex) : () => {}}
              onDragUpdate={isTop ? onDragUpdate : () => {}}
              onSpeak={isTop ? onSpeak : () => {}}
              isFavorited={isFavorited(word.id)}
              onToggleFavorite={isTop ? () => onToggleFavorite(word.id) : () => {}}
              stackStyle={{
                transform: `scale(${config.scale}) translateY(${config.yOffset}px)`,
                zIndex: config.zIndex,
                pointerEvents: isTop ? "auto" : "none",
              }}
            />
          );
        })}
      </div>
    </CardContent>
  );
}
